import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../helpers/Modal';
import { KnightKey } from '../../types';
import { capitalize } from '../../utils/capitalize';
import { SWORN_DATA } from '../../gameData';
import { GameEntityCard } from '../ui/GameEntityCard';
import { getIconPath } from '../../utils/iconPath';

interface CompendiumKnightsModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialKnight: KnightKey | null;
}

const KnightAssetSection = React.memo(({ 
    title, 
    items 
}: { 
    title: string; 
    items: [string, { description: string }][]; 
}) => (
    <div>
        <h4 className="text-lg font-semibold text-white mb-2">{title}</h4>
        <div className="space-y-2">
            {items.map(([key, data]) => (
                <div key={key} className="p-2 bg-slate-700 rounded-md"><p className="font-bold text-yellow-400 capitalize">{key.split('_').join(' ')}</p><p className="text-xs text-slate-300">{data.description || 'No description available.'}</p></div>
            ))}
        </div>
    </div>
));

const KnightDetails = React.memo(({ selectedKnight }: { selectedKnight: KnightKey }) => {
    const knightData = SWORN_DATA.classes[selectedKnight];

    return (
        <div className="p-4 bg-slate-800 rounded-lg border border-sworn-brown max-h-[50vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-sworn-gold mb-2">{capitalize(selectedKnight)}</h3>
            <p className="text-slate-300 mb-4">{knightData.base_description}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <KnightAssetSection title="Weapons" items={Object.entries(knightData.weapons)} />
                <KnightAssetSection title="Spells" items={Object.entries(knightData.spells)} />
            </div>
        </div>
    );
});

const CompendiumKnightsModal: React.FC<CompendiumKnightsModalProps> = ({ isOpen, onClose, initialKnight }) => {
    const knightKeys = Object.keys(SWORN_DATA.classes) as KnightKey[];
    const [selectedKnight, setSelectedKnight] = useState<KnightKey | null>(initialKnight);

    useEffect(() => {
        if (isOpen) {
            setSelectedKnight(initialKnight);
        }
    }, [isOpen, initialKnight]);

    const handleSelectKnight = useCallback((key: string) => setSelectedKnight(key as KnightKey), []);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Build Index: Knights" size="4xl">
            <div className="flex flex-wrap gap-4 justify-center mb-6">
                {knightKeys.map(key => (
                    <GameEntityCard
                        key={key}
                        id={key}
                        title={capitalize(key)}
                        iconPath={getIconPath('knight', key)}
                        isSelected={selectedKnight === key}
                        onClick={handleSelectKnight}
                        className="w-32" // Added width constraint for consistency
                    />
                ))}
            </div>
            {selectedKnight && <KnightDetails selectedKnight={selectedKnight} />}
        </Modal>
    );
};

export default CompendiumKnightsModal;